import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  UserPlus,
  Search,
  MoreHorizontal,
  UserMinus,
  Mail,
  Phone,
  Loader2,
} from 'lucide-react'
import { cn } from '@/lib/utils'

// Mock data
const mockTeamMembers = [
  { id: '1', username: 'partner001', name: 'สมชาย ใจดี', email: 'somchai@test.com', phone: '081-234-5678', joined_at: '2025-06-15', status: 'active' },
  { id: '2', username: 'partner002', name: 'วิชัย มั่งมี', email: 'wichai@test.com', phone: '082-345-6789', joined_at: '2025-07-20', status: 'active' },
  { id: '3', username: 'partner003', name: 'พิมพ์ใจ สวยงาม', email: 'pimjai@test.com', phone: '083-456-7890', joined_at: '2025-08-10', status: 'active' },
  { id: '4', username: 'partner004', name: 'อนุชา รวยเร็ว', email: 'anucha@test.com', phone: '084-567-8901', joined_at: '2025-09-05', status: 'active' },
  { id: '5', username: 'partner005', name: 'สุภาพร สดใส', email: 'supaporn@test.com', phone: '085-678-9012', joined_at: '2025-10-01', status: 'inactive' },
]

const mockAvailablePartners = [
  { id: '10', username: 'partner010', name: 'ธนกร เก่งมาก' },
  { id: '11', username: 'partner011', name: 'สมหญิง ใจงาม' },
  { id: '12', username: 'partner012', name: 'วิทยา ฉลาดมาก' },
]

export function TeamManagement() {
  const [isLoading, setIsLoading] = useState(true)
  const [teamMembers, setTeamMembers] = useState(mockTeamMembers)
  const [searchQuery, setSearchQuery] = useState('')
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [showRemoveDialog, setShowRemoveDialog] = useState(false)
  const [selectedMember, setSelectedMember] = useState<typeof mockTeamMembers[0] | null>(null)
  const [partnerSearch, setPartnerSearch] = useState('')
  const [isAdding, setIsAdding] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 500)
    return () => clearTimeout(timer)
  }, [])

  const filteredMembers = teamMembers.filter(m =>
    m.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.username.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const filteredAvailable = mockAvailablePartners.filter(p =>
    p.name.toLowerCase().includes(partnerSearch.toLowerCase()) ||
    p.username.toLowerCase().includes(partnerSearch.toLowerCase())
  )

  const handleAddPartner = async (partner: typeof mockAvailablePartners[0]) => {
    setIsAdding(true)
    // Simulate API call
    await new Promise(r => setTimeout(r, 1000))
    setTeamMembers([...teamMembers, {
      ...partner,
      email: `${partner.username}@test.com`,
      phone: '08x-xxx-xxxx',
      joined_at: new Date().toISOString().split('T')[0],
      status: 'active'
    }])
    setIsAdding(false)
    setShowAddDialog(false)
    setPartnerSearch('')
  }

  const handleRemovePartner = async () => {
    if (!selectedMember) return
    // Simulate API call
    await new Promise(r => setTimeout(r, 500))
    setTeamMembers(teamMembers.filter(m => m.id !== selectedMember.id))
    setShowRemoveDialog(false)
    setSelectedMember(null)
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-64 w-full" />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">จัดการทีม</h1>
          <p className="text-muted-foreground">เพิ่มหรือลบ Partners ในทีมของคุณ</p>
        </div>
        <Button onClick={() => setShowAddDialog(true)} className="gap-2">
          <UserPlus className="h-4 w-4" />
          เพิ่ม Partner
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="ค้นหา Partner..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Team Members Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Partners ในทีม ({teamMembers.length})</CardTitle>
          <CardDescription>รายชื่อ Partners ทั้งหมดในทีมของคุณ</CardDescription>
        </CardHeader>
        <CardContent>
          {filteredMembers.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <UserPlus className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>ไม่พบ Partner ในทีม</p>
              <p className="text-sm">เพิ่ม Partner เข้าทีมเพื่อเริ่มต้น</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Partner</TableHead>
                  <TableHead>ติดต่อ</TableHead>
                  <TableHead className="text-center">สถานะ</TableHead>
                  <TableHead>เข้าร่วม</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredMembers.map((member) => (
                  <TableRow key={member.id} className="hover:bg-muted/50">
                    <TableCell>
                      <div>
                        <p className="font-medium">{member.name}</p>
                        <p className="text-xs text-muted-foreground">@{member.username}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Mail className="h-3 w-3" />
                          {member.email}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Phone className="h-3 w-3" />
                          {member.phone}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge 
                        variant="secondary" 
                        className={cn(
                          "border-0",
                          member.status === 'active' ? "bg-win/10 text-win" : "bg-muted text-muted-foreground"
                        )}
                      >
                        {member.status === 'active' ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{member.joined_at}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => {
                              setSelectedMember(member)
                              setShowRemoveDialog(true)
                            }}
                          >
                            <UserMinus className="h-4 w-4 mr-2" />
                            นำออกจากทีม
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add Partner Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>เพิ่ม Partner เข้าทีม</DialogTitle>
            <DialogDescription>
              ค้นหาและเลือก Partner ที่ต้องการเพิ่มเข้าทีม
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="ค้นหา Partner..."
                value={partnerSearch}
                onChange={(e) => setPartnerSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="max-h-[300px] overflow-y-auto space-y-2">
              {filteredAvailable.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">
                  ไม่พบ Partner ที่ค้นหา
                </p>
              ) : (
                filteredAvailable.map((partner) => (
                  <div
                    key={partner.id}
                    className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                  >
                    <div>
                      <p className="font-medium">{partner.name}</p>
                      <p className="text-xs text-muted-foreground">@{partner.username}</p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleAddPartner(partner)}
                      disabled={isAdding}
                    >
                      {isAdding ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <UserPlus className="h-4 w-4 mr-1" />
                          เพิ่ม
                        </>
                      )}
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Remove Partner Confirmation */}
      <AlertDialog open={showRemoveDialog} onOpenChange={setShowRemoveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>ยืนยันการนำออกจากทีม</AlertDialogTitle>
            <AlertDialogDescription>
              คุณต้องการนำ <strong>{selectedMember?.name}</strong> ออกจากทีมใช่หรือไม่?
              การดำเนินการนี้ไม่สามารถย้อนกลับได้
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>ยกเลิก</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemovePartner}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              นำออกจากทีม
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
